import { URLS } from '@/constants';
import { VolumeDiscount, VolumeDiscountApplied, VolumeDiscountNew } from '@/services/api/models/pricing';
import { apiSlugSession } from '@/services/session';
import { buildPath } from '@/utils';
import JSONTransform from '@/utils/json-transform';

/**
 * @param {VolumeDiscount} discount data for Volume Discount update
 */
export const updateVolumeDiscount = async (discount: VolumeDiscount) => {
  const url = buildPath(URLS.PRICING.VOLUME_DISCOUNT, { id: discount.id });
  const params = new JSONTransform({ errorFormat: 'jsonapi' }).toSnakeCaseKeys();
  const response = await apiSlugSession.put<VolumeDiscount>(url, discount, { params });

  return response.data;
};

/**
 * @param {VolumeDiscount} discount data for Volume Discount creation
 */
export const createVolumeDiscount = async (discount: VolumeDiscountNew) => {
  const url = URLS.PRICING.VOLUME_DISCOUNTS;
  const params = new JSONTransform({ errorFormat: 'jsonapi' }).toSnakeCaseKeys();
  const response = await apiSlugSession.post<VolumeDiscount>(url, discount, { params });

  return response.data;
};

/**
 * Gets all Volume Discounts for all available products for brands that are visible to the active company.
 * (excludes discounts for unavailable and backordered products)
 *
 * If a brandId is provided, only discounts for products from that brand will be returned.
 */
export const getVolumeDiscountsForBrands = async (brandId?: number) => {
  const url = URLS.PRICING.VOLUME_DISCOUNTS_FOR_BRANDS;
  const params = new JSONTransform({ errorFormat: 'jsonapi', brandCompanyId: brandId }).toSnakeCaseKeys();
  const response = await apiSlugSession.get<VolumeDiscount[]>(url, { params });

  return response.data;
};

/**
 * Get Volume Discounts from API
 */
export const getVolumeDiscounts = async () => {
  const url = URLS.PRICING.VOLUME_DISCOUNTS;
  const params = new JSONTransform({ errorFormat: 'jsonapi' }).toSnakeCaseKeys();
  const response = await apiSlugSession.get<VolumeDiscount[]>(url, { params });

  return response.data;
};

/**
 * Get Volume Discounts by Id from API
 * @param id - Volume discount id to get data
 * @returns Volume discount data
 */
export const getVolumeDiscountById = async (id: VolumeDiscount['id']) => {
  const url = buildPath(URLS.PRICING.VOLUME_DISCOUNT, { id });
  const params = new JSONTransform({ errorFormat: 'jsonapi' }).toSnakeCaseKeys();
  const response = await apiSlugSession.get<VolumeDiscount>(url, { params });

  return response.data;
};

interface RemoveVolumeDiscountArgs {
  id: VolumeDiscount['id'];
}

/**
 * Remove Volume Discount from API
 * @param id - Volume discount id to remove
 */
export const removeVolumeDiscount = async ({ id }: RemoveVolumeDiscountArgs): Promise<{ id: number }> => {
  const url = buildPath(URLS.PRICING.VOLUME_DISCOUNT, { id });
  const params = new JSONTransform({ errorFormat: 'jsonapi' }).toSnakeCaseKeys();

  await apiSlugSession.delete(url, { params });

  return { id };
};

export interface UpdateVolumeDiscountStatusArgs {
  id: number;
  status: string;
}

/**
 * Update Volume Discounts status
 */
export const updateVolumeDiscountStatus = async ({
  id,
  status,
}: UpdateVolumeDiscountStatusArgs): Promise<{ id: number }> => {
  const url = buildPath(URLS.PRICING.VOLUME_DISCOUNT_UPDATE_STATUS, { id });
  const params = new JSONTransform({ errorFormat: 'jsonapi' }).toSnakeCaseKeys();

  await apiSlugSession.post(url, { status }, { params });

  return { id };
};

/**
 * Gets all the applied Volume Discounts for the given order.
 */
export const getVolumeDiscountsForOrder = async (uuid: string) => {
  const url = buildPath(URLS.PRICING.VOLUME_DISCOUNTS_FOR_ORDER, { uuid });
  const params = new JSONTransform({ errorFormat: 'jsonapi' }).toSnakeCaseKeys();
  const response = await apiSlugSession.get<VolumeDiscountApplied[]>(url, { params });

  return response.data;
};
